import { Bullet, Label, Scrollbar, Tooltip, array, p50, percent } from "@amcharts/amcharts5";
import { AxisRendererX, AxisRendererY, CategoryAxis, ColumnSeries, ValueAxis, XYChart } from "@amcharts/amcharts5/xy";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { PLAN_MODULE } from "@/constants";
import { createDimensionNames, createMetricsValues } from "@/helpers/dataMapping";
import { axisTitle } from "@/settings/charts";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { chartFontSettings, chartInitialSettings } from "@/settings/widgets";
import { ChartDataRecord, TooltipSeries } from "@/types/charts";

export default class extends BaseChart {
  names = [] as string[];
  metrics = [] as string[];

  initChart() {
    this.root.container.children.clear();

    if (this.chart) {
      (this.chart as XYChart).series.clear();
    }

    this.createChart();
    this.createAxes();

    this.metrics.forEach((name: string, index: number) => {
      const paletteLength = this.palette.length;
      const indexColor = index % paletteLength;
      this.createSeries(name, indexColor, name);
    });

    this.showLegend(this.metrics, (this.chart as XYChart).series.values as LegendData);
  }

  protected createChart() {
    this.chart = this.root.container.children.push(
      XYChart.new(this.root, {
        ...chartInitialSettings,
        panX: false,
        panY: false,
        wheelX: "panY",
        wheelY: "zoomY",
        layout: this.layout,
      })
    );

    (this.chart as XYChart).set(
      "scrollbarY",
      Scrollbar.new(this.root, {
        orientation: "vertical",
        marginLeft: 20,
      })
    );
  }

  protected createAxes() {
    const xRenderer = AxisRendererX.new(this.root, {});
    xRenderer.labels.template.setAll({ ...chartFontSettings.label });
    const x = (this.chart as XYChart).xAxes.push(
      ValueAxis.new(this.root, {
        min: 0,
        strictMinMax: true,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.displayMetric, this.data),
        renderer: xRenderer,
      })
    ) as ValueAxis<AxisRendererX>;

    const yRenderer = AxisRendererY.new(this.root, {});
    yRenderer.labels.template.setAll({ ...chartFontSettings.label });
    const y = (this.chart as XYChart).yAxes.push(
      CategoryAxis.new(this.root, {
        categoryField: "name",
        renderer: yRenderer,
        tooltip: Tooltip.new(this.root, {}),
      })
    ) as CategoryAxis<AxisRendererY>;

    y.data.setAll(this.data);

    this.axes = { x, y };

    if (this.settings.xAxisName) {
      this.axes.x.children.push(
        Label.new(this.root, {
          text: this.settings.xAxisName,
          ...chartFontSettings.axisTitle,
          x: p50,
          marginTop: axisTitle.marginTop,
          centerX: p50,
        })
      );
    }

    if (this.settings.yAxisName) {
      this.axes.y?.children.push(
        Label.new(this.root, {
          text: this.settings.yAxisName,
          ...chartFontSettings.axisTitle,
          x: percent(-30),
          rotation: -90,
          y: percent(50),
          centerX: p50,
          marginTop: axisTitle.marginTop,
        })
      );
    }
  }

  protected mapData(data: []): ChartDataRecord[] {
    let mappedData = [] as ChartDataRecord[];

    this.names = [];
    this.metrics = [];

    const firstDimension = this.settings.decompositionMetric;
    const secondDimension = this.settings.decompositionMetricSecondary;
    const displayMetric = this.settings.displayMetric;

    const preparedData = data as ChartDataRecord[];

    createDimensionNames(preparedData, firstDimension, this.names);
    createDimensionNames(preparedData, secondDimension, this.metrics);

    createMetricsValues(mappedData, preparedData, firstDimension, secondDimension, displayMetric, this.names);

    const findTotal = (param: Record<string, unknown>) =>
      Object.values(param).reduce((acc: number, curr) => (typeof curr === "number" ? acc + curr : acc), 0);

    mappedData.sort((a, b) => findTotal(a) - findTotal(b));
    mappedData = this.showItems(mappedData, "name", undefined, "first");
    mappedData = mappedData.filter((item) => findTotal(item) !== 0 && !!item.name);

    return mappedData;
  }

  protected createSeries(name: string, index: number, fieldName: string) {
    const shouldShowMetricValue = this.module === PLAN_MODULE ? Boolean(this.settings.showDataLabels) : false;

    const series = (this.chart as XYChart).series.push(
      ColumnSeries.new(this.root, {
        name: name as string,
        stacked: true,
        xAxis: this.axes.x,
        yAxis: this.axes.y as CategoryAxis<AxisRendererY>,
        baseAxis: this.axes.y,
        valueXField: fieldName as string,
        categoryYField: "name",
        fill: this.getSeriesColor(index),
      })
    );

    this.createTooltip(series as TooltipSeries, (target) => {
      let param: Record<string, unknown>[] = [];
      const tooltipValues = target?.dataItem?.dataContext as Record<string, unknown>;
      const xValue = series.get("valueXField");
      if (tooltipValues && xValue) {
        param = [
          {
            key: this.settings.decompositionMetric,
            value: tooltipValues.name,
          },
          {
            key: this.settings.decompositionMetricSecondary,
            value: xValue,
          },
          { key: this.settings.displayMetric, value: tooltipValues[xValue] },
        ];
      }
      return param;
    });

    series.columns.template.setAll({
      tooltipText: "{value}",
      tooltipY: percent(90),
    });

    series.data.setAll(this.data);

    series.appear();

    const formatterConfig = this.getDataLabelsFormatterConfig();
    const metricKey = this.settings.displayMetric;
    const metricFormatter = this.formatters?.[metricKey];
    const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);

    series.bullets.push(() => {
      const label = Label.new(this.root, {
        text: "{valueXField}",
        ...chartFontSettings.label,
        fill: this.getFill(series),
        centerY: p50,
        centerX: p50,
        populateText: true,
        textAlign: "center",
        fontSize: labelFontSize,
      });

      label.adapters.add("text", (labelText, target) => {
        const baseText = labelText ?? "";
        const rawValue = Number(target?.dataItem?.get("valueX" as never));

        if (!Number.isFinite(rawValue) || rawValue === 0) {
          return baseText;
        }

        if (!shouldShowMetricValue) {
          return baseText;
        }

        const formatted = metricFormatter
          ? metricFormatter(rawValue, formatterConfig)
          : this.root.numberFormatter.format(rawValue, this.getMetricFormatter(metricKey, formatterConfig));

        if (!formatted || formatted === baseText) {
          return baseText;
        }

        return `${baseText}\n${formatted}`;
      });

      label.adapters.add("visible", (visible) => {
        return visible;
      });

      return Bullet.new(this.root, {
        sprite: label,
      });
    });

    series.columns.template.onPrivate("width", (width, target) => {
      const thresholdValue = Number(this.settings?.dataLabelsHideThreshold);
      const hideThreshold = Number.isFinite(thresholdValue) && thresholdValue > 0 ? thresholdValue : 60;

      array.each(target?.dataItem?.bullets || [], (bullet) => {
        const sprite = bullet.get("sprite");
        if (!sprite) {
          return;
        }

        if (Number(width) > hideThreshold) {
          sprite.show();
        } else {
          sprite.hide();
        }
      });
    });
  }
}
